import React, { createContext, useContext, useMemo, useState } from 'react'

/**
 * Shared layout state for the chats split-view (list column + open chat).
 *
 * Lets the Chats list be collapsed to a slim counts rail, and lets an active
 * co-browse session force the list out of the way entirely (so the agent gets
 * just the visitor's screen + the chat). ChatThread — a sibling of the list —
 * flips `cobrowseActive`; the workspace grid reads `collapsed`.
 */
interface ChatLayoutValue {
  /** Effective: the list should be out of full-width (user collapsed OR co-browse). */
  collapsed: boolean
  /** The user's manual collapse toggle (independent of co-browse). */
  userCollapsed: boolean
  toggleUser: () => void
  setUserCollapsed: (v: boolean) => void
  /** True while a co-browse session is active — hides the list entirely. */
  cobrowseActive: boolean
  setCobrowseActive: (v: boolean) => void
}

const ChatLayoutContext = createContext<ChatLayoutValue | null>(null)

// Safe defaults so components (e.g. the standalone co-browse viewer) that render
// ChatThread-adjacent hooks outside a provider don't crash.
const NOOP: ChatLayoutValue = {
  collapsed: false,
  userCollapsed: false,
  toggleUser: () => {},
  setUserCollapsed: () => {},
  cobrowseActive: false,
  setCobrowseActive: () => {},
}

export const useChatLayout = (): ChatLayoutValue => useContext(ChatLayoutContext) ?? NOOP

export const ChatLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userCollapsed, setUserCollapsed] = useState(false)
  const [cobrowseActive, setCobrowseActive] = useState(false)

  const value = useMemo<ChatLayoutValue>(
    () => ({
      collapsed: userCollapsed || cobrowseActive,
      userCollapsed,
      toggleUser: () => setUserCollapsed((v) => !v),
      setUserCollapsed,
      cobrowseActive,
      setCobrowseActive,
    }),
    [userCollapsed, cobrowseActive],
  )

  return <ChatLayoutContext.Provider value={value}>{children}</ChatLayoutContext.Provider>
}
